import { useState } from 'react';
import type { SupportTicket, TicketStatus } from '@command-floor/shared-types';

interface Props {
  initialTickets: SupportTicket[];
}

const COLUMNS: { status: TicketStatus; label: string; accent: string }[] = [
  { status: 'OPEN',        label: 'Open',        accent: '#63b3ed' },
  { status: 'IN_PROGRESS', label: 'In Progress', accent: '#f6ad55' },
  { status: 'ON_HOLD',     label: 'On Hold',     accent: '#a0aec0' },
  { status: 'RESOLVED',    label: 'Resolved',    accent: '#48bb78' },
];

const TRANSITIONS: Record<TicketStatus, { label: string; next: TicketStatus }[]> = {
  OPEN:        [{ label: 'Start',   next: 'IN_PROGRESS' }],
  IN_PROGRESS: [{ label: 'Hold',    next: 'ON_HOLD'     }, { label: 'Resolve', next: 'RESOLVED'    }],
  ON_HOLD:     [{ label: 'Resume',  next: 'IN_PROGRESS' }, { label: 'Resolve', next: 'RESOLVED'    }],
  RESOLVED:    [{ label: 'Reopen',  next: 'OPEN'        }],
};

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export function SupportTicketBoard({ initialTickets }: Props) {
  const [tickets, setTickets] = useState<SupportTicket[]>(initialTickets);
  const [loading, setLoading] = useState(false);

  async function refresh() {
    setLoading(true);
    try {
      const res = await fetch('/api/support-tickets', { credentials: 'include' });
      if (res.ok) setTickets(await res.json());
    } finally {
      setLoading(false);
    }
  }

  async function moveTicket(ticketId: string, status: TicketStatus) {
    const res = await fetch(`/api/support-tickets/${ticketId}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    });
    if (!res.ok) return;
    const updated = await res.json();
    setTickets((prev) =>
      prev.map((t) =>
        t.id === ticketId
          ? { ...t, status: updated.status, resolvedAt: updated.resolvedAt }
          : t,
      ),
    );
  }

  async function deleteTicket(ticketId: string) {
    if (!window.confirm('Delete this ticket? This cannot be undone.')) return;
    const res = await fetch(`/api/support-tickets/${ticketId}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    if (res.ok || res.status === 204) {
      setTickets((prev) => prev.filter((t) => t.id !== ticketId));
    }
  }

  async function downloadAttachment(ticketId: string, fileName: string) {
    const res = await fetch(`/api/support-tickets/${ticketId}/attachment`, {
      credentials: 'include',
    });
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  }

  const total = tickets.length;
  const open = tickets.filter((t) => t.status === 'OPEN').length;
  const inProgress = tickets.filter((t) => t.status === 'IN_PROGRESS').length;
  const resolved = tickets.filter((t) => t.status === 'RESOLVED').length;

  return (
    <div>
      {/* Stats */}
      <div className="mb-6 grid grid-cols-4 gap-3">
        {[
          { label: 'Total', value: total, color: 'text-slate-200' },
          { label: 'Open', value: open, color: 'text-[#63b3ed]' },
          { label: 'In Progress', value: inProgress, color: 'text-[#f6ad55]' },
          { label: 'Resolved', value: resolved, color: 'text-[#48bb78]' },
        ].map(({ label, value, color }) => (
          <div key={label} className="rounded-lg border border-aurora bg-surface-panel px-4 py-3">
            <p className={`text-2xl font-bold ${color}`}>{value}</p>
            <p className="text-xs text-slate-500">{label}</p>
          </div>
        ))}
      </div>

      {/* Header */}
      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm text-slate-400">{total} total ticket{total !== 1 ? 's' : ''}</p>
        <button
          onClick={refresh}
          disabled={loading}
          className="rounded-md border border-aurora px-3 py-1.5 text-xs text-slate-400 hover:border-aurora-strong hover:text-slate-200 disabled:opacity-50"
        >
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {total === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-slate-500">
          <p className="text-4xl mb-3">📭</p>
          <p className="text-sm">No tickets yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-4 gap-4">
          {COLUMNS.map(({ status, label, accent }) => {
            const col = tickets.filter((t) => t.status === status);
            return (
              <div key={status}>
                <div className="mb-3 flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: accent }} />
                  <span className="text-sm font-medium text-slate-300">{label}</span>
                  <span className="ml-auto rounded-full bg-slate-800 px-2 py-0.5 text-xs text-slate-400">
                    {col.length}
                  </span>
                </div>
                <div className="flex flex-col gap-3">
                  {col.map((ticket) => (
                    <TicketCard
                      key={ticket.id}
                      ticket={ticket}
                      onMove={moveTicket}
                      onDelete={deleteTicket}
                      onDownload={downloadAttachment}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function TicketCard({
  ticket,
  onMove,
  onDelete,
  onDownload,
}: {
  ticket: SupportTicket;
  onMove: (id: string, status: TicketStatus) => void;
  onDelete: (id: string) => void;
  onDownload: (id: string, fileName: string) => void;
}) {
  return (
    <div className="rounded-lg border border-aurora bg-surface-panel p-3">
      {/* Top row: type badge + delete */}
      <div className="mb-2 flex items-center justify-between">
        <span
          className={`rounded-full px-2 py-0.5 text-xs font-medium ${
            ticket.type === 'BUG'
              ? 'bg-red-900/40 text-red-300'
              : 'bg-purple-900/40 text-purple-300'
          }`}
        >
          {ticket.type === 'BUG' ? 'Bug' : 'Feature'}
        </span>
        <button
          onClick={() => onDelete(ticket.id)}
          className="text-slate-600 hover:text-red-400 text-sm leading-none"
          title="Delete ticket"
        >
          ×
        </button>
      </div>

      {/* Description */}
      <p className="mb-2 line-clamp-3 text-sm text-slate-300">{ticket.description}</p>

      {/* Submitter + time */}
      <p className="mb-1 text-xs text-slate-500">
        {ticket.submittedBy.displayName} · {timeAgo(ticket.createdAt)}
      </p>

      {/* Attachment */}
      {ticket.attachmentFileName && (
        <button
          onClick={() => onDownload(ticket.id, ticket.attachmentFileName!)}
          className="mb-2 flex items-center gap-1 text-xs text-flag-blue hover:underline"
        >
          <span>📎</span>
          <span className="truncate max-w-[120px]">{ticket.attachmentFileName}</span>
        </button>
      )}

      {/* Status transitions */}
      <div className="mt-2 flex flex-wrap gap-1.5">
        {TRANSITIONS[ticket.status].map(({ label, next }) => (
          <button
            key={next}
            onClick={() => onMove(ticket.id, next)}
            className="rounded-md border border-aurora px-2 py-1 text-xs text-slate-400 hover:border-aurora-strong hover:text-slate-200"
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}
